// src/App.tsx
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import Layout from './components/Layout';
import KBUpload from './components/KBUpload';
import KBList from './components/KBList';
import Playground from './components/Playground';
import ConversationBrowser from './components/ConversationBrowser';
import PromptManager from './components/PromptManager';
import FallbackMessageManager from './components/FallbackMessageManager';
import QueryLog from './components/QueryLog';
import CompanyQuestionsList from './components/CompanyQuestionsList';
import CompanyQuestionsEditor from './components/CompanyQuestionsEditor';

import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import PrivateRoute from './components/PrivateRoute';
import Settings from './pages/Settings';

type UserRole = 'user' | 'admin' | 'superadmin';

function decodeJwtPayload(jwt: string): any {
  try {
    return JSON.parse(atob(jwt.split('.')[1])) || {};
  } catch {
    return {};
  }
}

function App() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [username, setUsername] = useState<string>('');
  const [userRole, setUserRole] = useState<UserRole>('user');

  const handleLogin = (jwt: string, name?: string) => {
    console.log('Login successful, setting token:', jwt);
    setToken(jwt);
    localStorage.setItem('token', jwt);

    const payload = decodeJwtPayload(jwt);
    console.log('Decoded JWT payload:', payload);
    setUsername(
      name ??
      payload?.username ??
      payload?.name ??
      'User'
    );
    setUserRole((payload?.userRole ?? 'user') as UserRole); 
  };

 const handleLogout = () => {
    setToken(null);
    setUsername('');
    setUserRole('user'); 
    localStorage.removeItem('token');
  };

    useEffect(() => {
    if (!token) return;
    const payload = decodeJwtPayload(token);
    if (!username) {
      setUsername(payload?.username ?? payload?.name ?? 'User');
    }
    setUserRole((payload?.userRole ?? payload?.role ?? 'user') as UserRole); // <-- NEW
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />

        {/* Private (wrapped inside Layout) */}
        <Route
          path="/"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <Navigate to="/kb-upload" />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/kb-upload"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <KBUpload token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/kb-list"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <KBList
                  token={token!}
                  onNavigateToPlayground={(kbId) => {
                    sessionStorage.setItem('selectedKbId', kbId.toString());
                    window.location.href = '/playground';
                  }}
                />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/playground"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <Playground token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/conversations"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <ConversationBrowser token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/querylog"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <QueryLog />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/prompt-mgr"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <PromptManager token={token!} userRole={userRole} />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/fallback-mgr"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <FallbackMessageManager token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        {/* ✅ New: Settings (API key management lives here) */}
        <Route
          path="/settings"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <Settings token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        {/* Company Questions Management */}
        <Route
          path="/company-questions"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <CompanyQuestionsList token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/company-questions/new"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <CompanyQuestionsEditor token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        <Route
          path="/company-questions/edit/:id"
          element={
            <PrivateRoute token={token}>
              <Layout onLogout={handleLogout} username={username}>
                <CompanyQuestionsEditor token={token!} />
              </Layout>
            </PrivateRoute>
          }
        />

        {/* Fallback */}
        <Route path="*" element={<Navigate to={token ? '/kb-upload' : '/login'} replace />} />
      </Routes>
    </Router>
  );
}

export default App;
